import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clientService from '../../../services/clientService';
import { CreditCard, Search, Filter, User, FolderKanban, Calendar, DollarSign, Plus, Edit, Trash2, Printer } from 'lucide-react';

const PaymentsList = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [clientId, setClientId] = useState('all');
  const [projectId, setProjectId] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [stats, setStats] = useState({
    total_payments: 0,
    paid_amount: 0,
    pending_amount: 0,
    overdue_amount: 0,
    this_month_revenue: 0
  });

  useEffect(() => { fetchInit(); }, []);
  useEffect(() => { fetchPayments(); }, [status, clientId, projectId]);

  const fetchInit = async () => {
    try {
      const c = await clientService.getClients({ per_page: 100 });
      setClients(c.data || []);
      const p = await clientService.getProjects({ per_page: 100 });
      setProjects(p.data || []);
      fetchPayments();
      fetchStats();
    } catch(e) { console.error(e); }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (status !== 'all') params.status = status;
      if (clientId !== 'all') params.client_id = clientId;
      if (projectId !== 'all') params.project_id = projectId;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await clientService.getPayments(params);
      setPayments(res.data || []);
    } catch (e) {
      console.error('Error loading payments', e);
      alert('Error loading payments');
    } finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await clientService.getPaymentStatistics({ from_date: fromDate || undefined, to_date: toDate || undefined });
      setStats(res);
    } catch(e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await clientService.deletePayment(id);
      fetchPayments();
      fetchStats();
    } catch (e) { alert('Error deleting payment'); }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-primary mb-2">Payment Management</h1>
        <p className="text-gray-600">Track all client and project payments</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Total Payments</p><p className="text-3xl font-bold text-primary mt-2">{stats.total_payments}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Paid Amount</p><p className="text-3xl font-bold text-green-600 mt-2">฿{(stats.paid_amount||0).toLocaleString()}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Pending</p><p className="text-3xl font-bold text-orange-600 mt-2">฿{(stats.pending_amount||0).toLocaleString()}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">Overdue</p><p className="text-3xl font-bold text-red-600 mt-2">฿{(stats.overdue_amount||0).toLocaleString()}</p></div>
        <div className="bg-white rounded-xl shadow-md p-6"><p className="text-gray-500 text-sm">This Month</p><p className="text-3xl font-bold text-purple-600 mt-2">฿{(stats.this_month_revenue||0).toLocaleString()}</p></div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input value={search} onChange={(e)=>setSearch(e.target.value)} onKeyDown={(e)=> e.key==='Enter' && fetchPayments()} placeholder="Search by invoice" className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary"/>
          </div>
          <select value={status} onChange={(e)=>setStatus(e.target.value)} className="px-4 py-2 border-2 rounded-lg">
            {['all','paid','pending','overdue','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={clientId} onChange={(e)=>setClientId(e.target.value)} className="px-4 py-2 border-2 rounded-lg">
            <option value="all">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
          <select value={projectId} onChange={(e)=>setProjectId(e.target.value)} className="px-4 py-2 border-2 rounded-lg">
            <option value="all">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} className="px-3 py-2 border-2 rounded-lg"/>
            <input type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} className="px-3 py-2 border-2 rounded-lg"/>
          </div>
        </div>
        <div className="mt-4 flex justify-between">
          <button onClick={()=>{fetchPayments(); fetchStats();}} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center gap-2"><Filter className="w-4 h-4"/>Apply</button>
          <Link to="/admin/payments/new" className="bg-primary hover:bg-primary-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2"><Plus className="w-5 h-5"/>Add Payment</Link>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary-700">
          <h2 className="text-xl font-bold text-white">All Payments ({payments.length})</h2>
        </div>
        {loading ? (<div className="p-10 text-center">Loading...</div>) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Client / Project</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.length === 0 ? (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No payments found</td></tr>
                ) : (
                  payments.map(pm => (
                    <tr key={pm.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-semibold">{pm.invoice_number}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col text-sm">
                          <span className="flex items-center gap-2"><User className="w-4 h-4"/>{pm.client?.company_name || '—'}</span>
                          {pm.project && <span className="flex items-center gap-2 text-gray-600"><FolderKanban className="w-4 h-4"/>{pm.project.project_name}</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">฿{Number(pm.amount).toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm"><div className="flex items-center gap-2"><Calendar className="w-4 h-4"/>{pm.payment_date}</div></td>
                      <td className="px-6 py-4"><span className={`px-3 py-1 rounded-full text-xs font-semibold ${pm.status==='paid'?'bg-green-100 text-green-800':pm.status==='pending'?'bg-orange-100 text-orange-800':pm.status==='overdue'?'bg-red-100 text-red-800':'bg-gray-100 text-gray-800'}`}>{pm.status}</span></td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/admin/payments/invoice/${pm.id}`} className="text-primary hover:text-primary-700 p-2 hover:bg-blue-50 rounded" title="Print Invoice"><Printer className="w-5 h-5"/></Link>
                          <Link to={`/admin/payments/edit/${pm.id}`} className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded" title="Edit"><Edit className="w-5 h-5"/></Link>
                          <button onClick={()=>handleDelete(pm.id)} className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-5 h-5"/></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsList;
